import { useCallback, useRef, useEffect, useMemo } from "react";
import noop from "shared/util/noop";

const DEFAULT_DELAY = 3000;

/**
 * Set a timer and execute a callback when it is complete.
 *
 * @param {Object} params - The params containing a delay and callback.
 * @param {number} params.delay - the duration of the timer.
 * @param {function} params.callback - the function to invoke when the timer is complete.
 *
 * @typedef {Object} Timer
 * @property {Function} start Starts timer.
 * @property {Function} stop Stops timer.
 * @return {Timer} - functions to start and stop the timer.
 */
function useTimeout({ delay = DEFAULT_DELAY, callback = noop }) {
	const timer = useRef();

	const start = useCallback(() => {
		clearTimeout(timer.current);

		timer.current = setTimeout(() => {
			timer.current = null;
			callback();
		}, delay);
	}, [callback, delay]);

	const stop = useCallback(() => {
		clearTimeout(timer.current);
		timer.current = null;
	}, []);

	// clear the timer when the component unmounts
	useEffect(() => () => {
		clearTimeout(timer.current);
	}, []);

	return useMemo(() => ({ start, stop }), [start, stop]);
}

export default useTimeout;



// WEBPACK FOOTER //
// ./src/shared/hooks/useTimeout.js